import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import TypeaheadSelect from './TypeaheadSelect';
import { useAccountsLookup, useContactsLookup, useOpportunitiesLookup, useUsersLookup, useUpdateTask, errorMessage } from '../api/queries';
import { useToast } from '../context/ToastContext';

// Editing an action item after it exists — the title, the due date, who it
// is for, what it is about. One modal, used from the list, from Today and
// from an account. `onSaved` is for callers that keep tasks outside the
// query cache (the account page) and need to reload after a save.
export default function ActionItemEditModal({ task, isOpen, onClose, onSaved }) {
  const toast = useToast();
  const { data: users = [] } = useUsersLookup(isOpen);
  const { data: accounts = [] } = useAccountsLookup(isOpen);
  const { data: contacts = [] } = useContactsLookup(isOpen);
  const { data: opportunities = [] } = useOpportunitiesLookup(isOpen);
  const update = useUpdateTask();
  const [form, setForm] = useState(null);
  const [accountText, setAccountText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !task) return;
    setForm({
      title: task.title || '',
      due_date: task.due_date ? String(task.due_date).slice(0, 16) : '',
      assigned_to: task.assigned_to || '',
      account_id: task.account_id || '',
      contact_id: task.contact_id || '',
      opportunity_id: task.opportunity_id || '',
      status: task.status || 'Open',
    });
    setAccountText(task.account_name || '');
    setError('');
  }, [isOpen, task]);

  const accountContacts = useMemo(() => contacts.filter((c) => !form || !form.account_id || c.account_id === Number(form.account_id)), [contacts, form]);
  const accountOpps = useMemo(() => opportunities.filter((o) => !form || !form.account_id || o.account_id === Number(form.account_id)), [opportunities, form]);

  if (!isOpen || !form) return null;

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('The title is required'); return; }
    try {
      await update.mutateAsync({ id: task.id, data: {
        title: form.title.trim(),
        due_date: form.due_date || null,
        assigned_to: form.assigned_to || null,
        account_id: form.account_id || null,
        contact_id: form.contact_id || null,
        opportunity_id: form.opportunity_id || null,
        status: form.status,
      } });
      toast.success('Action item updated');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the action item'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Action Item">
      <form onSubmit={save}>
        <div className="modal-body">
          {error && <div className="alert alert-error" role="alert">{error}</div>}
          <div className="form-group">
            <label className="form-label" htmlFor="edit-task-title">Action Item *</label>
            <input id="edit-task-title" type="text" className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label" htmlFor="edit-task-due">Due Date</label>
              <input id="edit-task-due" type="datetime-local" className="form-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="edit-task-assignee">Assign To</label>
              <select id="edit-task-assignee" className="form-select" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}>
                <option value="">Unassigned</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="edit-task-account">Related Account</label>
            <TypeaheadSelect
              id="edit-task-account"
              placeholder="Type to search accounts..."
              items={accounts}
              value={form.account_id}
              displayValue={accountText}
              onChange={(id, name) => { setForm({ ...form, account_id: id, contact_id: '', opportunity_id: '' }); setAccountText(name); }}
              showAddNewOption={false}
            />
          </div>
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label" htmlFor="edit-task-contact">Related Contact</label>
              <select id="edit-task-contact" className="form-select" value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} disabled={!form.account_id}>
                <option value="">None</option>
                {accountContacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="edit-task-opportunity">Related Opportunity</label>
              <select id="edit-task-opportunity" className="form-select" value={form.opportunity_id} onChange={(e) => setForm({ ...form, opportunity_id: e.target.value })} disabled={!form.account_id}>
                <option value="">None</option>
                {accountOpps.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="edit-task-status">Status</label>
            <select id="edit-task-status" className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="Open">Open</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={update.isPending}>Save Changes</button>
        </div>
      </form>
    </Modal>
  );
}
